import {
  Avatar,
  Box,
  Grid,
  Link,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { colorTheme } from 'theme';
import { emailCheck, forgotPassword } from 'service/AuthService';
import { useDispatch } from 'react-redux';
import { setSnackBarSuccess, setSnackBarFailed } from 'store/snackBar';
import LoadingButton from '@mui/lab/LoadingButton';
import { strings } from 'i18n/Strings';
import Logo from '../../../assets/images/Logo100X132.png';
import { type IErrorResponse } from 'types';

const ForgotPassword = (): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(theme.palette.mode);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [emailExist, setEmailExist] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [counter, setCounter] = useState(59);
  const [startCounter, setStartCounter] = useState(false);
  const validRegex = /\S+@\S+\.\S+/;
  const emailValidation = email.match(validRegex) != null;

  const handleErrorCheck = (): void => {
    if (email !== '') {
      if (emailValidation) {
        setEmailError('');
      } else {
        setEmailError(strings.displayText.emailValidateText);
      }
    } else {
      setEmailError('');
    }
  };

  useEffect(() => {
    handleErrorCheck();
  }, [email, emailExist]);

  useEffect(() => {
    if (email.match(validRegex) != null) {
      void (async () => {
        try {
          const data = await emailCheck(email);
          if (typeof data === 'object' && data !== null && 'exist' in data) {
            setEmailExist(Boolean(data.exist));
          }
        } catch (err: unknown) {
          const errorWithResponse = err as IErrorResponse; // type assertion
          setSnackBarFailed({
            snackBarMessage: errorWithResponse.response?.data?.message,
          });
        }
      })();
    }
  }, [email]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (startCounter && counter > 0) {
      timer = setTimeout(() => {
        setCounter(counter - 1);
      }, 1000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [counter, startCounter]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (email === '') {
      setEmailError(strings.displayText.emailMustText);
    }
    if (!emailError && email !== '') {
      void (async () => {
        setLoader(true);
        setStartCounter(true);
        setCounter(60);
        await forgotPassword(email)
          .then(() => {
            setLoader(false);
            setEmailError('');
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: strings.displayText.resetLinkSuccessText,
              }),
            );
          })
          .catch((err) => {
            setLoader(false);
            setEmailError(strings.displayText.emailNotRegisteredText);
            dispatch(
              setSnackBarFailed({ snackBarMessage: err.response.data.message }),
            );
          });
      })();
    }
  };
  const styles = {
    mainBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: 350,
      marginX: 'auto',
    },
    avatarStyles: {
      width: 100,
      height: 100,
      p: 2,
      bgcolor: colors.primaryBg[900],
      boxShadow: 3,
      position: 'relative',
      top: -48,
    },
    forgotPswdTypho: {
      fontFamily: 'Source Sans Pro !important',
      fontWeight: 700,
      marginBlock: 1,
      color: colors.greenAccent[500],
    },
    pleaseEnterTypho: {
      textAlign: 'center',
      color: colors.gray[400],
      fontWeight: 400,
      fontSize: 14,
    },
    formBox: {
      mt: 1,
      width: 350,
    },
    emailTextField: {
      input: {
        '&:-webkit-autofill': {
          '-webkit-box-shadow': '0 0 0 100px #eef9fa inset',
          '-webkit-text-fill-color': colors.greenAccent[100],
        },
      },
    },
    emailErr: {
      fontFamily: ['Inter', 'Source Sans Pro', 'sans-serif'].join(','),
      fontWeight: 400,
      fontSize: 14,
      letterSpacing: '-0.05px',
      position: 'absolute',
      color: colors.primaryRed[100],
    },
    resentLinkTypho: {
      mt: 3,
      mb: 2,
      bgcolor: colors.greenAccent[500],
      borderRadius: 2,
      py: 1.5,
    },
  };

  return (
    <Fragment>
      <Box sx={styles.mainBox}>
        <Avatar alt="Zen Pensions logo" sx={styles.avatarStyles}>
          <img width={46} height={56} src={Logo} />
        </Avatar>
        <Typography component="h1" variant="h2" sx={styles.forgotPswdTypho}>
          {strings.displayText.forgotPassword}
        </Typography>
        <Typography component="h5" variant="h5" sx={styles.pleaseEnterTypho}>
          {strings.displayText.pleaseEnterEmail}
        </Typography>
        <Box pb={5} />
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={styles.formBox}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email address"
            name="email"
            autoComplete="email"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEmail(e.target.value);
            }}
            sx={styles.emailTextField}
            autoFocus
          />
          <Typography component="h1" variant="body1" sx={styles.emailErr}>
            {emailError}
          </Typography>

          <Box pb={5} />
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loading={loader}
            loadingPosition="end"
            sx={styles.resentLinkTypho}
            onClick={() => {
              handleErrorCheck();
            }}
          >
            <Typography component="h1" variant="h5">
              {strings.displayText.requestResetLink}
            </Typography>
          </LoadingButton>
          <Grid container>
            <Grid item xs textAlign="center">
              <Link href="/" variant="body2" underline="none">
                <Typography component="h1" variant="h5">
                  {strings.displayText.signIn}
                </Typography>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Fragment>
  );
};
export default ForgotPassword;
